import { db, auth } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  startAfter,
  increment,
  writeBatch
} from 'firebase/firestore';
import { allWordsPresent } from '../utils/searchUtils';
import { uploadImage, uploadVideo } from './imageService';
import { DEFAULT_IMAGES } from '../constants/defaults';
import { normalizeCategoryId } from '../constants/categories';

export { allWordsPresent };

/**
 * Servicio de gestión de posts
 */

/**
 * Normalizar post viejo al nuevo formato (sin modificar Firestore)
 */
export const normalizePost = (post) => {
  // Extraer datos de authorInfo si existe
  const authorInfo = post.authorInfo || {};
  
  return {
    id: post.id,
    
    // Usuario - priorizar authorInfo
    userId: post.userId || post.userID || authorInfo.uid || '',
    userName: authorInfo.displayName || authorInfo.username || post.userName || 'Usuario',
    userPhotoURL: authorInfo.profileImage || post.userPhotoURL || post.photoURL || '',
    
    // Contenido
    caption: post.caption || post.description || post.text || '',
    text: post.text || post.caption || '',
    
    // Media - priorizar mediaUrls del viejo sistema
    media: (() => {
      // Primero intentar con mediaUrls (sistema viejo)
      if (post.mediaUrls && Array.isArray(post.mediaUrls) && post.mediaUrls.length > 0) {
        return post.mediaUrls.map(item => ({
          type: item.type || 'image',
          url: item.url,
          thumbnailUrl: item.thumbnailUrl || item.url
        }));
      }
      
      // Luego probar con media (sistema nuevo)
      if (post.media && Array.isArray(post.media) && post.media.length > 0) {
        return post.media;
      }
      
      // Fallbacks para otros formatos
      if (post.imageUrl) {
        return [{ type: 'image', url: post.imageUrl, thumbnailUrl: post.imageUrl }];
      }
      
      if (post.imageUrls && Array.isArray(post.imageUrls)) {
        return post.imageUrls.map(url => ({ 
          type: 'image', 
          url, 
          thumbnailUrl: url 
        }));
      }
      
      if (post.images && Array.isArray(post.images)) {
        return post.images.map(img => {
          if (typeof img === 'string') {
            return { type: 'image', url: img, thumbnailUrl: img };
          }
          return {
            type: img.type || 'image',
            url: img.url || img.imageUrl || img,
            thumbnailUrl: img.thumbnailUrl || img.url || img.imageUrl || img
          };
        });
      }
      
      return [];
    })(),
    
    // Tags
    tags: post.tags || post.hashtags || [],
    
    // Servicio
    isService: post.isService || false,
    servicePrice: post.servicePrice || post.price || 0,
    
    // Contadores
    likesCount: post.likesCount || post.likes || 0,
    commentsCount: post.commentsCount || post.commentCount || post.comments || 0,
    sharesCount: post.sharesCount || post.shares || 0,
    
    // Timestamps - asegurar que createdAt siempre esté disponible
    createdAt: post.createdAt || post.timestamp || null,
    updatedAt: post.updatedAt || null,
    timestamp: post.timestamp || post.createdAt || null, // Mantener timestamp para compatibilidad
    
    // Mantener campos extras por si acaso
    category: normalizeCategoryId(post.category) || post.category || null,
    isPublic: post.isPublic !== false, // default true
    isNsfw: post.isNsfw || false,
    likedBy: post.likedBy || [],

    // Preservar authorInfo para PremiumName y Avatar (isPremium, premiumNameColor, photoURLIsGif)
    authorInfo: {
      ...authorInfo,
      uid: authorInfo.uid || post.userId || post.userID || '',
      displayName: authorInfo.displayName || authorInfo.username || post.userName || 'Usuario',
      username: authorInfo.username || '',
      profileImage: authorInfo.profileImage || post.userPhotoURL || post.photoURL || '',
      isPremium: authorInfo.isPremium || post.isPremiumAuthor || false,
      premiumNameColor: authorInfo.premiumNameColor || post.premiumNameColor || null,
      photoURLIsGif: authorInfo.photoURLIsGif === true
    }
  };
};

/**
 * Obtener posts del feed (explorar todos)
 * @param {number} limitCount - Número de posts a obtener
 * @param {any} lastVisible - Último documento para paginación
 * @returns {Promise<object>}
 */
export const getExplorePosts = async (limitCount = 20, lastVisible = null) => {
  try {
    const postsRef = collection(db, 'posts');
    let q;

    // Intentar ordenar por createdAt, si falla usar timestamp
    try {
      if (lastVisible) {
        q = query(
          postsRef,
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(limitCount)
        );
      } else {
        q = query(
          postsRef,
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      }
    } catch (e) {
      // Fallback a timestamp si createdAt no existe
      if (lastVisible) {
        q = query(
          postsRef,
          orderBy('timestamp', 'desc'),
          startAfter(lastVisible),
          limit(limitCount)
        );
      } else {
        q = query(
          postsRef,
          orderBy('timestamp', 'desc'),
          limit(limitCount)
        );
      }
    }

    const snapshot = await getDocs(q);
    const posts = snapshot.docs.map(doc => normalizePost({
      id: doc.id,
      ...doc.data()
    }));

    const lastDoc = snapshot.docs[snapshot.docs.length - 1];

    return {
      success: true,
      posts,
      lastVisible: lastDoc,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error al obtener posts:', error);
    return {
      success: false,
      error: error.message,
      posts: []
    };
  }
};

/**
 * Buscar posts por término (caption, userName, description).
 * Para sugerencias o búsqueda limitada.
 * @param {string} searchTerm - Término de búsqueda
 * @param {number} limitCount - Máximo de posts a traer de Firestore (luego se filtra en cliente)
 * @returns {Promise<{ success: boolean, posts: Array, error?: string }>}
 */
export const searchPosts = async (searchTerm, limitCount = 10) => {
  try {
    const term = (searchTerm || '').trim();
    if (!term) {
      return { success: true, posts: [] };
    }
    const postsRef = collection(db, 'posts');
    let q;
    try {
      q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        limit(Math.max(limitCount, 50))
      );
    } catch (e) {
      q = query(
        postsRef,
        orderBy('timestamp', 'desc'),
        limit(Math.max(limitCount, 50))
      );
    }
    const snapshot = await getDocs(q);
    const posts = snapshot.docs
      .map(doc => normalizePost({ id: doc.id, ...doc.data() }))
      .filter(post => {
        const caption = post.caption || '';
        const userName = post.userName || post.displayName || '';
        const description = post.description || '';
        const combined = `${caption} ${userName} ${description}`;
        return allWordsPresent(combined, term);
      })
      .slice(0, limitCount);
    return { success: true, posts };
  } catch (error) {
    console.error('Error en searchPosts:', error);
    return { success: false, posts: [], error: error.message };
  }
};

/**
 * Extrae la primera URL de imagen de un post (datos crudos de Firestore).
 * Prefiere thumbnailUrl cuando exista para mejor calidad en collage/hero.
 * @param {object} post - Documento post (id + data())
 * @returns {string|null}
 */
function getFirstImageUrlFromPost(post) {
  if (post.mediaUrls && Array.isArray(post.mediaUrls) && post.mediaUrls.length > 0) {
    const first = post.mediaUrls[0];
    return (first.thumbnailUrl || first.url) || null;
  }
  if (post.media && Array.isArray(post.media) && post.media.length > 0) {
    const m = post.media[0];
    if (typeof m === 'string') return m;
    return (m.thumbnailUrl || m.url || m.imageUrl) || null;
  }
  if (post.imageUrl) return post.imageUrl;
  if (post.imageUrls && Array.isArray(post.imageUrls) && post.imageUrls.length > 0) {
    const first = post.imageUrls[0];
    if (typeof first === 'string') return first;
    return (first.thumbnailUrl || first.url || first.imageUrl) || null;
  }
  if (post.images && Array.isArray(post.images) && post.images.length > 0) {
    const img = post.images[0];
    if (typeof img === 'string') return img;
    return (img.thumbnailUrl || img.url || img.imageUrl) || null;
  }
  return null;
}

/**
 * Fisher-Yates shuffle (in-place).
 * @param {Array} arr
 */
function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Obtiene URLs de imágenes de posts recientes para el hero/banner.
 * Query a posts, filtra por existencia de media, extrae primera URL, shuffle, slice.
 * @param {number} limitCount - Número de URLs a devolver (ej. 12)
 * @returns {Promise<string[]>}
 */
export const getHeroImageUrls = async (limitCount = 12) => {
  try {
    const postsRef = collection(db, 'posts');
    let q;
    try {
      q = query(postsRef, orderBy('createdAt', 'desc'), limit(50));
    } catch (e) {
      q = query(postsRef, orderBy('timestamp', 'desc'), limit(50));
    }
    const snapshot = await getDocs(q);
    const urls = snapshot.docs
      .map(doc => getFirstImageUrlFromPost({ id: doc.id, ...doc.data() }))
      .filter(Boolean);
    const shuffled = shuffle([...urls]);
    return shuffled.slice(0, limitCount);
  } catch (error) {
    console.error('Error en getHeroImageUrls:', error);
    return [];
  }
};

/**
 * Obtener posts de un usuario específico
 * @param {string} userId - ID del usuario
 * @param {number} limitCount - Número de posts
 * @param {any} lastVisible - Último documento
 * @returns {Promise<object>}
 */
export const getUserPosts = async (userId, limitCount = 50, lastVisible = null) => {
  try {
    const postsRef = collection(db, 'posts');
    let q;

    try {
      if (lastVisible) {
        q = query(
          postsRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(limitCount)
        );
      } else {
        q = query(
          postsRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      }
    } catch (e) {
      // Fallback a userID si userId no existe
      if (lastVisible) {
        q = query(
          postsRef,
          where('userID', '==', userId),
          orderBy('timestamp', 'desc'),
          startAfter(lastVisible),
          limit(limitCount)
        );
      } else {
        q = query(
          postsRef,
          where('userID', '==', userId),
          orderBy('timestamp', 'desc'),
          limit(limitCount)
        );
      }
    }

    const snapshot = await getDocs(q);
    const posts = snapshot.docs.map(doc => normalizePost({
      id: doc.id,
      ...doc.data()
    }));

    const lastDoc = snapshot.docs[snapshot.docs.length - 1];

    return {
      success: true,
      posts,
      lastVisible: lastDoc,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error al obtener posts del usuario:', error);
    return {
      success: false,
      error: error.message,
      posts: []
    };
  }
};

/**
 * Obtener posts a los que el usuario ha dado like (para tab "Me gusta" del perfil)
 * @param {string} userId - ID del usuario (currentUser.uid)
 * @param {number} limitCount - Máximo de likes a considerar (default 50)
 * @returns {Promise<{ success: boolean, posts?: Array, error?: string }>}
 */
export const getUserLikedPosts = async (userId, limitCount = 50) => {
  try {
    const likesRef = collection(db, 'likes');
    const q = query(
      likesRef,
      where('userId', '==', userId),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    const postIds = [...new Set(snapshot.docs.map(d => d.data().postId).filter(Boolean))];
    if (postIds.length === 0) {
      return { success: true, posts: [] };
    }
    const posts = [];
    const chunkSize = 10;
    for (let i = 0; i < postIds.length; i += chunkSize) {
      const chunk = postIds.slice(i, i + chunkSize);
      const snapshots = await Promise.all(chunk.map(id => getDoc(doc(db, 'posts', id))));
      snapshots.forEach((snap, idx) => {
        if (snap.exists()) {
          posts.push(normalizePost({ id: snap.id, ...snap.data() }));
        }
      });
    }
    return { success: true, posts };
  } catch (error) {
    console.error('Error al obtener posts con like:', error);
    return { success: false, posts: [], error: error.message };
  }
};

/**
 * Normalizar datos de servicio
 * @param {object} service - Datos del servicio
 * @returns {object}
 */
export const normalizeService = (service) => {
  return {
    id: service.id,
    
    // Usuario
    userId: service.userId || service.userID || service.uid || '',
    userName: service.userName || service.username || service.displayName || 'Usuario',
    userPhotoURL: service.userPhotoURL || service.photoURL || null,
    
    // Contenido
    caption: service.caption || service.title || service.name || '',
    description: service.description || service.details || '',
    media: service.media || service.mediaUrls || service.images || service.imageUrls || [],
    
    // Categoría y tags
    category: service.category || service.type || '',
    categoryName: service.categoryName || service.category || '',
    subcategory: service.subcategory || null,
    tags: service.tags || service.hashtags || [],
    
    // Servicio específico - Precio y paquetes
    servicePrice: service.servicePrice || service.price || 0,
    prices: service.prices || null, // Array de paquetes con precios
    currency: service.currency || 'ARS',
    isService: true, // Siempre true si viene de la colección services
    
    // Detalles del servicio
    deliveryTime: service.deliveryTime || null,
    specifications: service.specifications || null,
    terms: service.terms || null,
    
    // Opciones de personalización
    allowClientDescription: service.allowClientDescription !== false,
    allowCustomizations: service.allowCustomizations !== false,
    
    // Estado y disponibilidad
    status: service.status || 'active',
    workStatus: service.workStatus || 'open',
    isNSFW: service.isNSFW || false,
    
    // Contadores
    likesCount: service.likesCount || service.likes || 0,
    commentsCount: service.commentsCount || service.comments || 0,
    sharesCount: service.sharesCount || service.shares || 0,
    ordersCount: service.ordersCount || service.orders || 0,
    orders: service.orders || service.ordersCount || 0,
    views: service.views || 0,
    
    // Metadata
    createdAt: service.createdAt || service.timestamp || null,
    updatedAt: service.updatedAt || null,
    
    // Preservar campos custom que puedan existir
    customFields: service.customFields || {}
  };
};

/**
 * Obtener servicios de un usuario específico
 * Busca primero en colección "services", luego en "posts" con isService=true
 * @param {string} userId - ID del usuario
 * @param {number} limitCount - Número de servicios
 * @param {any} lastVisible - Último documento
 * @returns {Promise<object>}
 */
export const getUserServices = async (userId, limitCount = 40, lastVisible = null) => {
  try {
    console.log('🔍 Buscando servicios para userId:', userId);
    
    // ESTRATEGIA 1: Buscar en colección "services"
    try {
      console.log('📦 Intentando colección "services"...');
      const servicesRef = collection(db, 'services');
      let servicesQuery;

      if (lastVisible) {
        servicesQuery = query(
          servicesRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(limitCount)
        );
      } else {
        servicesQuery = query(
          servicesRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      }

      const servicesSnapshot = await getDocs(servicesQuery);
      
      if (servicesSnapshot.size > 0) {
        console.log(`✅ Encontrados ${servicesSnapshot.size} servicios en colección "services"`);
        const services = servicesSnapshot.docs.map(doc => normalizeService({
          id: doc.id,
          ...doc.data()
        }));

        const lastDoc = servicesSnapshot.docs[servicesSnapshot.docs.length - 1];

        return {
          success: true,
          services,
          lastVisible: lastDoc,
          hasMore: servicesSnapshot.docs.length === limitCount
        };
      }
      
      console.log('⚠️ No se encontraron servicios en colección "services"');
    } catch (servicesError) {
      console.log('⚠️ Error al buscar en "services":', servicesError.message);
      console.log('Intentando con colección "posts"...');
    }

    // ESTRATEGIA 2: Buscar en colección "posts" con isService=true
    console.log('📝 Intentando colección "posts" con isService=true...');
    const postsRef = collection(db, 'posts');
    let postsQuery;

    try {
      if (lastVisible) {
        postsQuery = query(
          postsRef,
          where('userId', '==', userId),
          where('isService', '==', true),
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(limitCount)
        );
      } else {
        postsQuery = query(
          postsRef,
          where('userId', '==', userId),
          where('isService', '==', true),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      }
    } catch (e) {
      // Fallback a userID si userId no existe
      console.log('⚠️ Fallback a userID...');
      if (lastVisible) {
        postsQuery = query(
          postsRef,
          where('userID', '==', userId),
          where('isService', '==', true),
          orderBy('timestamp', 'desc'),
          startAfter(lastVisible),
          limit(limitCount)
        );
      } else {
        postsQuery = query(
          postsRef,
          where('userID', '==', userId),
          where('isService', '==', true),
          orderBy('timestamp', 'desc'),
          limit(limitCount)
        );
      }
    }

    const postsSnapshot = await getDocs(postsQuery);
    console.log(`${postsSnapshot.size > 0 ? '✅' : '⚠️'} Encontrados ${postsSnapshot.size} servicios en colección "posts"`);
    
    const services = postsSnapshot.docs.map(doc => normalizePost({
      id: doc.id,
      ...doc.data()
    }));

    const lastDoc = postsSnapshot.docs[postsSnapshot.docs.length - 1];

    return {
      success: true,
      services,
      lastVisible: lastDoc,
      hasMore: postsSnapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('❌ Error al obtener servicios del usuario:', error);
    return {
      success: false,
      error: error.message,
      services: []
    };
  }
};

/**
 * Obtener posts de usuarios que sigo
 * @param {string} userId - ID del usuario actual
 * @param {number} limitCount - Número de posts
 * @param {any} lastVisible - Último documento
 * @returns {Promise<object>}
 */
export const getFollowingPosts = async (userId, limitCount = 20, lastVisible = null) => {
  try {
    // Primero obtener la lista de usuarios que sigo
    const userDoc = await getDoc(doc(db, 'users', userId));
    const following = userDoc.data()?.following || [];

    if (following.length === 0) {
      return {
        success: true,
        posts: [],
        lastVisible: null,
        hasMore: false
      };
    }

    const postsRef = collection(db, 'posts');
    // Firestore limita `in` a 10 elementos. Hacemos chunking y merge.
    const chunks = [];
    for (let i = 0; i < following.length; i += 10) {
      chunks.push(following.slice(i, i + 10));
    }

    const snapshots = await Promise.all(
      chunks.map((chunk) => {
        const base = [
          where('userId', 'in', chunk),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        ];
        const q = lastVisible ? query(postsRef, ...base, startAfter(lastVisible)) : query(postsRef, ...base);
        return getDocs(q);
      })
    );

    const byId = new Map();
    let mightHaveMore = false;
    snapshots.forEach((snap) => {
      if (snap.docs.length === limitCount) mightHaveMore = true;
      snap.docs.forEach((d) => {
        byId.set(d.id, normalizePost({ id: d.id, ...d.data() }));
      });
    });

    const posts = Array.from(byId.values()).sort((a, b) => {
      const aT = a?.createdAt?.toMillis ? a.createdAt.toMillis() : (a?.createdAt instanceof Date ? a.createdAt.getTime() : 0);
      const bT = b?.createdAt?.toMillis ? b.createdAt.toMillis() : (b?.createdAt instanceof Date ? b.createdAt.getTime() : 0);
      return bT - aT;
    });

    const sliced = posts.slice(0, limitCount);
    const lastCursor = sliced.length > 0 ? sliced[sliced.length - 1].createdAt : null;

    return {
      success: true,
      posts: sliced,
      lastVisible: lastCursor,
      hasMore: mightHaveMore || posts.length > limitCount
    };
  } catch (error) {
    console.error('Error al obtener posts de siguiendo:', error);
    return {
      success: false,
      error: error.message,
      posts: []
    };
  }
};

/**
 * Obtener posts populares (más likes)
 * @param {number} limitCount - Número de posts
 * @returns {Promise<object>}
 */
export const getPopularPosts = async (limitCount = 20) => {
  try {
    const postsRef = collection(db, 'posts');
    let q;

    try {
      q = query(
        postsRef,
        orderBy('likesCount', 'desc'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    } catch (e) {
      // Fallback a 'likes' si 'likesCount' no existe
      q = query(
        postsRef,
        orderBy('likes', 'desc'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    }

    const snapshot = await getDocs(q);
    const posts = snapshot.docs.map(doc => normalizePost({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      posts
    };
  } catch (error) {
    console.error('Error al obtener posts populares:', error);
    return {
      success: false,
      error: error.message,
      posts: []
    };
  }
};

/**
 * Obtener un post por ID
 * @param {string} postId - ID del post
 * @returns {Promise<object>}
 */
export const getPostById = async (postId) => {
  try {
    const postDoc = await getDoc(doc(db, 'posts', postId));
    
    if (postDoc.exists()) {
      return {
        success: true,
        post: normalizePost({
          id: postDoc.id,
          ...postDoc.data()
        })
      };
    } else {
      return {
        success: false,
        error: 'Post no encontrado'
      };
    }
  } catch (error) {
    console.error('Error al obtener post:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Obtener posts aleatorios para sección "Más para explorar".
 * Trae un batch reciente, filtra excludePostId, shuffle y toma limit.
 * @param {number} limit - Cantidad de posts a devolver (default 12)
 * @param {string|null} excludePostId - ID del post a excluir (ej. el actual)
 * @returns {Promise<{ success: boolean, posts: Array }>}
 */
export const getRandomPosts = async (limitCount = 12, excludePostId = null) => {
  try {
    const res = await getExplorePosts(40, null);
    if (!res.success || !res.posts?.length) {
      return { success: true, posts: [] };
    }
    let list = res.posts.filter((p) => p.id !== excludePostId);
    list = shuffle([...list]);
    return { success: true, posts: list.slice(0, limitCount) };
  } catch (error) {
    console.error('Error en getRandomPosts:', error);
    return { success: true, posts: [] };
  }
};

/**
 * Crear un nuevo post
 * @param {object} postData - Datos del post
 * @returns {Promise<object>}
 */
export const createPost = async (postData) => {
  try {
    const newPost = {
      ...postData,
      likesCount: 0,
      commentsCount: 0,
      sharesCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'posts'), newPost);

    return {
      success: true,
      postId: docRef.id,
      message: 'Post creado exitosamente'
    };
  } catch (error) {
    console.error('Error al crear post:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Subir imágenes y crear post (para el modal de crear post).
 * @param {string} caption - Texto del post
 * @param {File[]} files - Archivos de imagen (al menos 1)
 * @param {string} category - Categoría del post (default: 'general')
 * @returns {Promise<{ success: boolean, postId?: string, post?: object, error?: string }>}
 */
export const createPostWithMedia = async (caption, files, category = 'general') => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { success: false, error: 'Debes iniciar sesión para crear un post' };
    }
    if (!files || files.length === 0) {
      return { success: false, error: 'Añade al menos una imagen o video' };
    }

    let userDisplayName = currentUser.displayName || 'Usuario';
    let userPhotoURL = currentUser.photoURL || null;
    let username = null;
    let isPremium = false;
    let premiumNameColor = null;
    let photoURLIsGif = false;
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        userDisplayName = userData.displayName || userData.username || currentUser.displayName || 'Usuario';
        userPhotoURL = userData.photoURL || userData.profileImage || currentUser.photoURL || null;
        username = userData.username || null;
        isPremium = userData.isPremium === true;
        premiumNameColor = userData.premiumNameColor || null;
        photoURLIsGif = userData.photoURLIsGif === true;
      }
    } catch (userError) {
      console.warn('No se pudieron obtener datos del usuario:', userError);
    }

    if (!isPremium) {
      userPhotoURL = null;
      photoURLIsGif = false;
    }

    const media = [];
    const mediaUrls = [];
    const basePath = `posts/${currentUser.uid}`;
    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      const result = isVideo
        ? await uploadVideo(file, basePath, currentUser.uid)
        : await uploadImage(file, basePath, currentUser.uid);
      if (!result.success) throw new Error(result.error || (isVideo ? 'Error al subir video' : 'Error al subir imagen'));
      if (isVideo) {
        media.push({ type: 'video', url: result.url, thumbnailUrl: DEFAULT_IMAGES.VIDEO_POSTER || result.url });
        mediaUrls.push({ type: 'video', url: result.url, thumbnailUrl: DEFAULT_IMAGES.VIDEO_POSTER || result.url });
      } else {
        media.push({ type: 'image', url: result.url, thumbnailUrl: result.url });
        mediaUrls.push({ type: 'image', url: result.url, thumbnailUrl: result.url });
      }
    }

    const postData = {
      userId: currentUser.uid,
      userName: userDisplayName,
      userPhotoURL: userPhotoURL,
      caption: caption || '',
      text: caption || '',
      media,
      mediaUrls,
      category: category || 'general',
      authorInfo: {
        uid: currentUser.uid,
        displayName: userDisplayName,
        username: username,
        profileImage: userPhotoURL,
        isPremium,
        premiumNameColor,
        photoURLIsGif
      }
    };

    const createResult = await createPost(postData);
    if (!createResult.success) return createResult;

    const now = new Date();
    const postForFeed = normalizePost({
      id: createResult.postId,
      ...postData,
      likesCount: 0,
      commentsCount: 0,
      sharesCount: 0,
      createdAt: now,
      updatedAt: now
    });
    return {
      success: true,
      postId: createResult.postId,
      post: postForFeed
    };
  } catch (error) {
    console.error('Error en createPostWithMedia:', error);
    return {
      success: false,
      error: error.message || 'Error al crear el post'
    };
  }
};

/**
 * Actualizar authorInfo (isPremium, premiumNameColor) en todos los posts del usuario.
 * Útil para que posts antiguos reflejen el color premium actual tras un cambio de perfil.
 * @param {string} userId - ID del usuario
 * @param {object} authorInfo - { isPremium?, premiumNameColor?, displayName?, profileImage? }
 * @returns {Promise<{ success: boolean, updated: number, error?: string }>}
 */
export const updateAuthorInfoInUserPosts = async (userId, authorInfo) => {
  if (!userId) return { success: true, updated: 0 };
  try {
    const postsRef = collection(db, 'posts');
    const q = query(postsRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return { success: true, updated: 0 };

    const batch = writeBatch(db);
    const updates = {};
    if (authorInfo.isPremium !== undefined) updates['authorInfo.isPremium'] = authorInfo.isPremium;
    if (authorInfo.premiumNameColor !== undefined) updates['authorInfo.premiumNameColor'] = authorInfo.premiumNameColor;
    if (authorInfo.displayName !== undefined) updates['authorInfo.displayName'] = authorInfo.displayName;
    if (authorInfo.profileImage !== undefined) updates['authorInfo.profileImage'] = authorInfo.profileImage;
    if (authorInfo.username !== undefined) updates['authorInfo.username'] = authorInfo.username;
    if (authorInfo.photoURLIsGif !== undefined) updates['authorInfo.photoURLIsGif'] = authorInfo.photoURLIsGif;

    if (Object.keys(updates).length === 0) return { success: true, updated: 0 };

    updates.updatedAt = serverTimestamp();
    snapshot.docs.forEach((docSnap) => batch.update(docSnap.ref, updates));

    await batch.commit();
    return { success: true, updated: snapshot.size };
  } catch (error) {
    console.error('Error al actualizar authorInfo en posts:', error);
    return { success: false, updated: 0, error: error.message };
  }
};

/**
 * Actualizar un post
 * @param {string} postId - ID del post
 * @param {object} updates - Datos a actualizar
 * @returns {Promise<object>}
 */
export const updatePost = async (postId, updates) => {
  try {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return {
      success: true,
      message: 'Post actualizado'
    };
  } catch (error) {
    console.error('Error al actualizar post:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Eliminar un post
 * @param {string} postId - ID del post
 * @returns {Promise<object>}
 */
export const deletePost = async (postId) => {
  try {
    await deleteDoc(doc(db, 'posts', postId));
    
    return {
      success: true,
      message: 'Post eliminado'
    };
  } catch (error) {
    console.error('Error al eliminar post:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Dar like a un post
 * @param {string} postId - ID del post
 * @param {string} userId - ID del usuario que da like
 * @returns {Promise<object>}
 */
export const likePost = async (postId, userId) => {
  try {
    // Agregar like a la colección de likes
    await addDoc(collection(db, 'likes'), {
      postId,
      userId,
      createdAt: serverTimestamp()
    });

    // Incrementar contador en el post
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      likesCount: increment(1)
    });

    // Notificación in-app al dueño del post (si no es like a sí mismo)
    const postSnap = await getDoc(postRef);
    const postOwnerId = postSnap.exists() ? postSnap.data().userId : null;
    if (postOwnerId && postOwnerId !== userId) {
      const { createNotification } = await import('./notificationService');
      const userSnap = await getDoc(doc(db, 'users', userId));
      const actorData = userSnap.exists() ? userSnap.data() : {};
      await createNotification({
        userId: postOwnerId,
        type: 'like',
        payload: {
          postId,
          actorUserId: userId,
          actorUserName: actorData.displayName || actorData.username || 'Alguien',
          actorPhotoURL: actorData.photoURL || actorData.profileImage || null
        }
      });
    }

    return {
      success: true,
      message: 'Like agregado'
    };
  } catch (error) {
    console.error('Error al dar like:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Quitar like de un post
 * @param {string} postId - ID del post
 * @param {string} userId - ID del usuario
 * @returns {Promise<object>}
 */
export const unlikePost = async (postId, userId) => {
  try {
    // Buscar y eliminar el like
    const likesRef = collection(db, 'likes');
    const q = query(
      likesRef,
      where('postId', '==', postId),
      where('userId', '==', userId),
      limit(1)
    );

    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return {
        success: false,
        error: 'Like no encontrado'
      };
    }

    // Eliminar el like
    await deleteDoc(snapshot.docs[0].ref);

    // Decrementar contador en el post
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      likesCount: increment(-1)
    });

    return {
      success: true,
      message: 'Like eliminado'
    };
  } catch (error) {
    console.error('Error al quitar like:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
